import { Jaco } from "oc-jaco3d/arrangement/Jaco";
import { Jaco3d } from "oc-jaco3d/arrangement3d/Jaco3d";
import { v3 } from "oc-threejs/short3";
import * as Maths3 from "oc-threejs/Maths3";
import { simpleSizeFormatter } from "oc-threejs/objects/rulers/simpleSizeFormatter";
import { LengthRuler } from "oc-threejs/objects/rulers/LengthRuler";
import { colorFromStyle } from "../commons/Utils";
import { I18N } from "oc-I18N";
import * as Commons from "oc-commons/Commons";
import { LineBasicMaterial } from "three";
import { AbschnittRuler, RULER_FONT_HEIGHT } from "../arrangement/AbschnittRuler";

/**
 * Stellt LengthRuler um eine Box für ungefähr die folgende Kameraperspektive dar:
 *
 *              xtop
 *              ---
 *             +---+
 *    ztop  / /   /| |  yright
 *           +---+ +
 *   yleft | |   |/  /  zbottom
 *           +---+
 *            ---
 *          xbottom
 *
 */

const xbottom = {
  gridPoints: [v3(0, 0, 1), v3(1, 0, 1)],
  textOffset: v3(0, 0.02, 0),
  normal: v3(0, 1, 0),
  textQuaternion: Maths3.quaternion.x270,
};

const yleft = {
  gridPoints: [v3(0, 0, 0), v3(0, 1, 0)],
  textOffset: v3(-0.2, 0, 0),
  normal: v3(0, 0, 1),
  textQuaternion: Maths3.quaternion.zero,
};

const zbottom = {
  gridPoints: [v3(1, 0, 0), v3(1, 0, 1)],
  textOffset: v3(0, 0.2, 0),
  normal: v3(0, 1, 0),
  textQuaternion: Maths3.quaternion.zero,
};

// Die Z-Offsets (versatz) werden nicht mehr fest hier vergeben, sondern in
// Segment.updateRuler dynamisch gestapelt (siehe computeRulerVersatz in AbschnittRuler.ts).
// Hier verbleiben nur die rein darstellungsbezogenen Eigenschaften (Farbe, Hinweis, labelless).
export const rulerStyles = Commons.cache(() => ({
  GanzesSegment: {
    color: APP.settings.Easy_ZaunreiheRulerFarbe,
    hinweis: "",
  },
  Empty: {
    color: APP.settings.Easy_ZaunreiheRulerFarbe,
    hinweis: "",
    labelless: true,
  },
  Segment: {
    color: APP.settings.Easy_ZaunreiheRulerFarbe,
    hinweis: "",
  },
  Element: {
    color: APP.settings.Easy_ElementeRulerFarbe,
    hinweis: "",
  },
  Tor: {
    color: APP.settings.Easy_TorRulerFarbe,
    hinweis: "",
  },
  Schiebetorüberstand: {
    color: APP.settings.Easy_TorRulerFarbe,
    hinweis: "",
  },
  Überstand: {
    color: colorFromStyle("--color-error"),
    hinweis: "\n" + I18N("geht über Grundstückslinie hinaus"),
  },
  Leerraum: {
    color: APP.settings.Easy_LeerraumRulerFarbe,
    hinweis: "",
  },
}));

export class AbschnittRuler3d extends Jaco3d {
  CLASS_NAME = "AbschnittRuler3d";
  declare jaco: AbschnittRuler;

  lengthRuler: LengthRuler;

  constructor(jaco: Jaco) {
    super(jaco);
  }

  update3d() {
    super.update3d();
    if (
      this.changed("style") ||
      this.changed("x0") ||
      this.changed("x1") ||
      this.changed("versatz")
    ) {
      const options = rulerStyles()[this.jaco.style];
      const versatz = this.jaco.versatz;

      const box = this.jaco.parent.getBox();
      box.min.x = this.jaco.x0;
      box.max.x = this.jaco.x1;
      const points = xbottom.gridPoints.map((v) => box.grid(v));

      const finalOptions = {
        ...xbottom,
        point1: points[0],
        point2: points[1],
        offsets: [-versatz, 0, -versatz],
        showLines: true,
        showLinesIfLengthGreaterThen: 1,
        sizeFormatter: (size) => simpleSizeFormatter()(size) + options.hinweis,
        labelless: !!(options as any).labelless,
        fontHeight: RULER_FONT_HEIGHT,
        fontDepthTest: true,
        fontColor: "#" + options.color.getHexString(),
        backgroundColor: "rgba(255,255,255,0.01)", // Verhindert schwarzen Hintergrund bei Maßlabels
        negative: false,
        textAsSprite: false,
        onTap: null,
        moveTextUpinHeightMultiplies: 0,
        textCanvasCallback: undefined,
        pixelPerUnit: 500,
        createMeshMaterial: () => new LineBasicMaterial({ color: options.color }),
      };

      if (this.lengthRuler == null) {
        this.lengthRuler = new LengthRuler(finalOptions);
        this.add(this.lengthRuler);
      } else {
        this.lengthRuler.update(finalOptions);
      }
    }
  }

  // @ts-ignore
  clone(recursive?: boolean) {
    return new AbschnittRuler3d(this.jaco);
  }

  getBox() {
    return this.jaco.getBox();
  }
}
